import { trackEvent } from '$/logger';
import { type HarmonyFacet } from '$/pages/DashboardPages/pages/DesignGuide/stores/useDesignGuideStore';
import { type MaterialMapping } from '$/pages/InspirationEditorPage/lib/materialMappings';
import { queryClient } from '$/services/client';
import { type DetailedInspiration } from '$/services/usecases/inspirations/mapper/inspiration.schema';
import {
  type Harmony,
  type HarmonyWithMaterials,
} from '$/services/usecases/materials/mapper/harmonies';
import {
  harmoniesQuery,
  harmonyByIdQuery,
} from '$/services/usecases/materials/queries';

export const hasHarmonyMaterialForKey = (
  harmony: HarmonyWithMaterials | null | undefined,
  componentKey: string,
): boolean => !!harmony?.inspirationMaterials[componentKey];

export const resolvePreviousHarmonyWithMaterials = async (
  currentOverrides: Record<string, MaterialMapping>,
  initialHarmonyId?: string,
): Promise<HarmonyWithMaterials | null> => {
  const activeHarmony = await resolveActiveHarmony(currentOverrides);
  const harmonyId = activeHarmony?.id ?? initialHarmonyId;
  if (!harmonyId) return null;

  return (
    (await queryClient.ensureQueryData(harmonyByIdQuery(harmonyId))) ?? null
  );
};

export const mergeHarmonyOverridesPreservingManual = async (
  inspiration: DetailedInspiration,
  previousHarmony: HarmonyWithMaterials | null,
  newHarmony: HarmonyWithMaterials,
  currentOverrides: Record<string, MaterialMapping>,
): Promise<Record<string, MaterialMapping>> => {
  const { getMaterialOverrides } = await import(
    '$/pages/InspirationEditorPage/stores/useInspirationEditorStore'
  );

  const nextOverrides = await getMaterialOverrides(inspiration, newHarmony);
  const baselineOverrides = await getMaterialOverrides(
    inspiration,
    previousHarmony,
  );

  for (const [id, component] of Object.entries(inspiration.components)) {
    const key = component.key;

    if (
      hasHarmonyMaterialForKey(previousHarmony, key) ||
      hasHarmonyMaterialForKey(newHarmony, key)
    ) {
      continue;
    }

    const current = currentOverrides[id];
    const baseline = baselineOverrides[id];
    if (current && current.material.id !== baseline?.material.id) {
      nextOverrides[id] = current;
    }
  }

  return nextOverrides;
};

export const findActiveHarmony = (
  overrides: Record<string, MaterialMapping> | undefined,
  harmonies: Harmony[],
): Harmony | undefined => {
  if (!overrides) return undefined;

  const appliedMaterialIds = new Set(
    Object.values(overrides)
      .filter(
        (mapping) =>
          mapping.harmonyMaterialIndex != null &&
          mapping.harmonyMaterialIndex >= 0,
      )
      .map((mapping) => mapping.material.id),
  );
  if (appliedMaterialIds.size === 0) return undefined;

  return harmonies.find(
    (harmony) =>
      appliedMaterialIds.size === harmony.materials.length &&
      harmony.materials.every((m) => appliedMaterialIds.has(m.id)),
  );
};

export const resolveActiveHarmony = async (
  overrides: Record<string, MaterialMapping> | undefined,
): Promise<Harmony | undefined> => {
  const data = await queryClient.ensureQueryData(harmoniesQuery());
  const harmonies = data?.isSuccessful ? data.response.payload : [];
  return findActiveHarmony(overrides, harmonies);
};

export const shuffleHarmonyOverrides = (
  overrides: Record<string, MaterialMapping>,
  materials: HarmonyWithMaterials['materials'],
): Record<string, MaterialMapping> => {
  const hasHarmonyMaterial = (idx: number | undefined): idx is number =>
    idx !== undefined && idx >= 0 && idx < materials.length;

  for (let attempt = 0; attempt < 24; attempt++) {
    const perm = Array.from({ length: materials.length }, (_, i) => i);
    for (let i = perm.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [perm[i], perm[j]] = [perm[j], perm[i]];
    }

    const currentMaterials = Array.from<(typeof materials)[number] | undefined>(
      { length: materials.length },
    );
    for (const mapping of Object.values(overrides)) {
      const idx = mapping.harmonyMaterialIndex;
      if (hasHarmonyMaterial(idx)) currentMaterials[idx] = mapping.material;
    }

    const changed = perm.some(
      (newIdx, oldIdx) =>
        hasHarmonyMaterial(oldIdx) &&
        currentMaterials[oldIdx]?.id !== materials[newIdx]?.id,
    );
    if (!changed) continue;

    const nextOverrides = { ...overrides };
    for (const compId in nextOverrides) {
      const idx = nextOverrides[compId].harmonyMaterialIndex;
      if (!hasHarmonyMaterial(idx)) continue;
      nextOverrides[compId] = {
        ...nextOverrides[compId],
        material: materials[perm[idx]],
      };
    }

    return nextOverrides;
  }

  return { ...overrides };
};

export const trackHarmonyFilter = (
  facet: HarmonyFacet,
  value: string,
  scene: 'exterior' | 'interior',
) => {
  const category =
    scene === 'exterior' ? 'FilterDesignGuideEXT' : 'FilterDesignGuideINT';

  const action = (() => {
    switch (facet) {
      case 'mood':
        return 'Mood';
      case 'tonality':
        return 'Tonality';
      case 'colorRange':
        return 'Colorgroup';
      case 'sceneType':
        return scene === 'exterior' ? 'BuildingType' : 'RoomType';
      default:
        return null;
    }
  })();

  if (!action) return;

  trackEvent(category, action, value);
};
